import type { components } from '@/api/schema'
import { elapsedShare, FORECAST_HISTORY, forecastTotal } from '@/lib/forecast'
import { formatStatus } from '@/lib/utils'

export type HomeSummary = components['schemas']['DashboardSummary']
export type HomeMonth = components['schemas']['DashboardMonthFinancials']
export type HomeStatusCount = components['schemas']['DashboardStatusCount']
export type HomeFinancials = components['schemas']['DashboardFinancials']

// Same series colors as the Analytics page, so both pages read alike.
export const REVENUE_COLOR = '#2a78d6'
export const PROFIT_COLOR = '#1baf7a'
export const LOSS_COLOR = '#e5484d'
export const ORDERS_COLOR = '#6b7280'
export const BOOKED_COLOR = '#94a3b8'
export const GRID_COLOR = 'oklch(0.922 0 0)'
export const AXIS_TICK = { fontSize: 12, fill: 'oklch(0.556 0 0)' }

export const WINDOW_MONTHS = 12

export function num(value: string | number | null | undefined): number {
  const n = typeof value === 'number' ? value : Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

export function optionalNum(value: string | number | null | undefined): number | null {
  if (value == null) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const monthFormat = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' })
const monthYearFormat = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })

export function monthLabel(month: string) {
  return monthFormat.format(new Date(`${month}T00:00:00Z`))
}

export function monthYearLabel(month: string) {
  return monthYearFormat.format(new Date(`${month}T00:00:00Z`))
}

/** First day of the oldest month in the window, as YYYY-MM-DD. */
export function windowStart(now = new Date()) {
  const start = new Date(Date.UTC(now.getFullYear(), now.getMonth() - (WINDOW_MONTHS - 1), 1))
  return start.toISOString().slice(0, 10)
}

export type MonthForecast = { revenue: number; profit: number; orders: number }

/** The full-month figures for the window's last month while it is still under way (USA-1734). */
export function currentMonthForecast(months: HomeMonth[], now = new Date()): MonthForecast | null {
  const current = months.at(-1)
  if (!current) return null
  const start = new Date(`${current.month}T00:00:00Z`)
  const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1))
  const elapsed = elapsedShare(start, end, now)
  const history = months.slice(0, -1).slice(-FORECAST_HISTORY.month)
  const forecast = (value: (month: HomeMonth) => number) =>
    forecastTotal(value(current), history.map(value), elapsed)
  const revenue = forecast((month) => num(month.total_revenue))
  const profit = forecast((month) => num(month.total_profit))
  const orders = forecast((month) => month.order_count ?? 0)
  if (revenue == null || profit == null || orders == null) return null
  return { revenue, profit, orders: Math.round(orders) }
}

export const compactMoney =new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
})

// One color per status, progressing with the lifecycle: grays before work starts, light to dark
// blue while goods are ordered and moving, greens once delivered; amber holds, red problems.
const STATUS_COLORS: Record<string, string> = {
  not_sent: '#e5e7eb',
  ordering: '#d1d5db',
  sent: '#9ca3af',
  confirmed: '#bae6fd',
  processing: '#7dd3fc',
  backordered: '#f59e0b',
  part_backordered: '#fbbf24',
  on_hold: '#fde68a',
  to_schedule: '#7dd3fc',
  booked: '#38bdf8',
  at_terminal: '#0ea5e9',
  part_shipped: '#0284c7',
  in_transit: '#0369a1',
  part_delivered: '#6ee7b7',
  delivered: '#10b981',
  waiting_on_install: '#059669',
  needs_attention: '#ef4444',
  issue: '#ef4444',
  part_cancelled: '#fca5a5',
  cancelled: '#f87171',
}
const TONE_FALLBACK: Record<string, string> = {
  success: '#10b981',
  progress: '#0ea5e9',
  warning: '#f59e0b',
  danger: '#ef4444',
  neutral: '#9ca3af',
}

export function statusColor(status: { key: string; tone?: string | null }): string {
  return STATUS_COLORS[status.key] ?? TONE_FALLBACK[status.tone ?? 'neutral'] ?? '#9ca3af'
}

// Labels for the stage keys above, for the list filters the homepage bars link to.
const STAGE_LABELS: Record<string, string> = {
  ordering: 'Ordering',
  po_review: 'PO review',
  processing: 'Processing',
  not_sent: 'Not sent',
  sent: 'Sent',
  confirmed: 'Confirmed',
  backordered: 'Backordered',
  part_backordered: 'Part backordered',
  on_hold: 'On hold',
  to_schedule: 'To schedule',
  booked: 'Booked',
  at_terminal: 'At terminal',
  part_shipped: 'Part shipped',
  in_transit: 'In transit',
  part_delivered: 'Part delivered',
  delivered: 'Delivered',
  waiting_on_install: 'Waiting on install',
  needs_attention: 'Needs attention',
  issue: 'Issue',
  part_cancelled: 'Part cancelled',
  cancelled: 'Cancelled',
}

export function stageLabel(key: string): string {
  return STAGE_LABELS[key] ?? formatStatus(key)
}

export type Segment = { key: string; label: string; count: number; color: string }

export function segments(statuses: HomeStatusCount[]): Segment[] {
  return statuses.map((status) => ({
    key: status.key,
    label: status.label,
    count: status.count ?? 0,
    color: statusColor(status),
  }))
}
